import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Home,
  FileCheck,
  Send,
  Inbox,
  Shield,
  Settings,
  LogOut,
  Building2,
  Users,
  Truck,
  Search,
  Bell,
  ChevronRight,
  Menu,
  X,
  Heart,
  Car,
  Calendar,
  Activity,
  AlertTriangle
} from 'lucide-react';
import { useExchangeAuth } from '../../contexts/ExchangeAuthContext';

const roleConfig = {
  driver: {
    title: 'Driver Portal',
    color: 'neon-cyan',
    icon: Users,
    navItems: [
      { path: '/portal/driver', label: 'Dashboard', icon: Home },
      { path: '/portal/driver/credentials', label: 'My Credentials', icon: FileCheck },
      { path: '/portal/driver/passport', label: 'My Passport', icon: Shield },
      { path: '/portal/driver/requests', label: 'Access Requests', icon: Inbox },
      { path: '/portal/driver/trust', label: 'Trusted Partners', icon: Shield },
      { path: '/portal/driver/settings', label: 'Settings', icon: Settings },
    ]
  },
  provider: {
    title: 'Provider Portal',
    color: 'neon-green',
    icon: Building2,
    navItems: [
      { path: '/portal/provider', label: 'Dashboard', icon: Home },
      { path: '/portal/provider/compliance', label: 'Compliance', icon: Activity, highlight: true },
      { path: '/portal/provider/credentials', label: 'Company Credentials', icon: FileCheck },
      { path: '/portal/provider/drivers', label: 'Driver Roster', icon: Users },
      { path: '/portal/provider/requests', label: 'Incoming Requests', icon: Inbox },
      { path: '/portal/provider/outgoing', label: 'Outgoing Requests', icon: Send },
      { path: '/portal/provider/trust', label: 'Trusted Partners', icon: Shield },
      { path: '/portal/provider/settings', label: 'Settings', icon: Settings },
    ]
  },
  broker: {
    title: 'Broker Portal',
    color: 'neon-purple',
    icon: Search,
    navItems: [
      { path: '/portal/broker', label: 'Dashboard', icon: Home },
      { path: '/portal/broker/compliance', label: 'Network Compliance', icon: Activity },
      { path: '/portal/broker/search', label: 'Search Providers', icon: Search },
      { path: '/portal/broker/credentials', label: 'My Credentials', icon: FileCheck },
      { path: '/portal/broker/requests', label: 'My Requests', icon: Send },
      { path: '/portal/broker/trust', label: 'Trusted Partners', icon: Shield },
      { path: '/portal/broker/verified', label: 'Verified Providers', icon: Shield },
      { path: '/portal/broker/settings', label: 'Settings', icon: Settings },
    ]
  },
  patient: {
    title: 'Patient Portal',
    color: 'neon-cyan',
    icon: Heart,
    navItems: [
      { path: '/portal/patient', label: 'Dashboard', icon: Home },
      { path: '/portal/patient/documents', label: 'My Documents', icon: FileCheck },
      { path: '/portal/patient/rides', label: 'My Rides', icon: Car },
      { path: '/portal/patient/schedule', label: 'Schedule', icon: Calendar },
      { path: '/portal/patient/providers', label: 'My Providers', icon: Building2 },
      { path: '/portal/patient/settings', label: 'Settings', icon: Settings },
    ]
  }
};

export const PortalLayout = ({ children, role }) => {
  const { user, organization, logout } = useExchangeAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  
  const config = roleConfig[role] || roleConfig.driver;
  const RoleIcon = config.icon;

  const handleLogout = () => {
    logout();
    navigate('/xchange');
  };

  return (
    <div className="min-h-screen bg-medex-bg flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-medex-surface border-r border-white/5
        transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-white/5">
            <Link to="/xchange" className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-xl bg-${config.color}/20 flex items-center justify-center`}>
                <RoleIcon className={`w-5 h-5 text-${config.color}`} />
              </div>
              <div>
                <div className="text-white font-semibold text-sm">OVA Xchange</div>
                <div className={`text-${config.color} text-xs`}>{config.title}</div>
              </div>
            </Link>
          </div>

          {/* User info */}
          <div className="p-4 border-b border-white/5">
            <div className="text-sm">
              <div className="text-white font-medium">
                {user?.first_name} {user?.last_name}
              </div>
              <div className="text-slate-500 text-xs">{user?.email}</div>
              {organization && (
                <div className={`text-${config.color} text-xs mt-1`}>
                  {organization.legal_name}
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {config.navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const ItemIcon = item.icon;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors relative
                    ${isActive 
                      ? `bg-${config.color}/10 text-${config.color}` 
                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                    }
                    ${item.highlight ? 'ring-1 ring-neon-green/50' : ''}
                  `}
                >
                  <ItemIcon className="w-5 h-5" />
                  <span className="text-sm">{item.label}</span>
                  {item.highlight && (
                    <span className="absolute right-2 w-2 h-2 rounded-full bg-neon-green animate-pulse" />
                  )}
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-white/5">
            <button
              onClick={handleLogout}
              className="flex items-center space-x-3 px-3 py-2.5 rounded-lg w-full text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-sm">Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="h-16 border-b border-white/5 flex items-center justify-between px-4 lg:px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 text-slate-400 hover:text-white"
          >
            <Menu className="w-6 h-6" />
          </button>

          <div className="flex items-center space-x-4 ml-auto">
            {/* Notifications */}
            <button className="relative p-2 text-slate-400 hover:text-white">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default PortalLayout;
